import {
  Button,
  Form,
  Input,
  Flex,
  Select,
  InputNumber,
  DatePicker,
  Space,
} from "antd";
import { FORM_REQUIRED } from "../../../configs/configs";
import { useEffect, useState, useContext } from "react";
import useFetch from "../../../hooks/useFetch";
import { IndexContext } from "../../../context/IndexContext";
import dayjs from "dayjs";

const { TextArea } = Input;

function EmissionFactorForm({
  mode,
  setIsEmissionFactorFormModalOpen,
  refreshTable,
  initialValues,
}) {
  const [fetchWrapper] = useFetch();

  // full emission source fetched from backend
  const [fullEmissionSourceList, setFullEmissionSourceList] = useState([]);
  const [fullEmissionSourceListLoading, setFullEmissionSourceListLoading] =
    useState(false);

  // selection for gas type
  const [gasTypeSelected, setGasTypeSelected] = useState(null);
  const [gasTypeList, setGasTypeList] = useState([]);
  const [gasTypeListLoading, setGasTypeListLoading] = useState(false);

  // selection for scope
  const [scopeSelected, setScopeSelected] = useState(null);
  const [scopeList, setScopeList] = useState([]);
  const [scopeListLoading, setScopeListLoading] = useState(false);

  // selection for scope type
  const [scopeTypeSelected, setScopeTypeSelected] = useState(null);
  const [scopeTypeList, setScopeTypeList] = useState([]);
  const [scopeTypeListLoading, setScopeTypeListLoading] = useState(false);
  const requiredCategoryTypeScopeTypeList = [6, 7, 8, 9]; // TODO: find a not so hard code way to determine required (maybe can pass the scope type id that required category type from backend)

  // selection for category type
  const [categoryTypeSelected, setCategoryTypeSelected] = useState(null);
  const [categoryTypeList, setCategoryTypeList] = useState([]);
  const [categoryTypeListLoading, setCategoryTypeListLoading] = useState(false);

  // selection for emission source
  const [emissionSourceSelected, setEmissionSourceSelected] = useState(null);
  const [emissionSourceList, setEmissionSourceList] = useState([]);
  const [emissionSourceListLoading, setEmissionSourceListLoading] =
    useState(false);

  // selection for dimension
  const [dimensionSelected, setDimensionSelected] = useState(null);
  const [dimensionList, setDimensionList] = useState([]);
  const [dimensionListLoading, setDimensionListLoading] = useState(false);

  // selection for factor unit (denominator)
  const [factorUnitDenominatorSelected, setFactorUnitDenominatorSelected] =
    useState(null);
  const [factorUnitDenominatorList, setFactorUnitDenominatorList] = useState(
    []
  );
  const [
    factorUnitDenominatorListLoading,
    setFactorUnitDenominatorListLoading,
  ] = useState(false);

  // selection for region
  const [regionSelected, setRegionSelected] = useState(null);
  const [regionList, setRegionList] = useState([]);
  const [regionListLoading, setRegionListLoading] = useState(false);

  // loading states
  const [loadingSubmitButton, setLoadingSubmitButton] = useState(false);
  const loading =
    scopeListLoading ||
    scopeTypeListLoading ||
    categoryTypeListLoading ||
    dimensionListLoading;

  const [emissionFactorForm] = Form.useForm();

  // load index context
  const { messageApi, authenticationState } = useContext(IndexContext);

  /* API to get full emission source list */
  function getFullEmissionSource() {
    setFullEmissionSourceListLoading(true);
    fetchWrapper({
      endpoint_url: "emission_factor_management/getFullEmissionSourceList",
      body: {
        mode: mode,
      },
      onSuccess: (response) => {
        setFullEmissionSourceList(response.data);
        setFullEmissionSourceListLoading(false);
      },
    });
  }

  /* API to get gas type */
  function getGasType() {
    setGasTypeListLoading(true);
    fetchWrapper({
      endpoint_url: "emission_factor_management/getGasTypeList",
      // body: {
      //   scope_id: scopeSelected,
      //   scope_type_id: scopeTypeSelected,
      //   category_type_id: categoryTypeSelected ? categoryTypeSelected : null,
      //   emission_source: emissionSourceSelected,
      //   dimension_id: dimensionSelected,
      //   mode: mode,
      // },
      onSuccess: (response) => {
        setGasTypeList(response.data);
        setGasTypeListLoading(false);
      },
    });
  }

  /* API to get factor unit (denominator) */
  function getFactorUnitDenominator() {
    setFactorUnitDenominatorListLoading(true);
    fetchWrapper({
      endpoint_url: "emission_factor_management/getFactorUnitDenominatorList",
      body: {
        dimension_id: dimensionSelected,
      },
      onSuccess: (response) => {
        setFactorUnitDenominatorList(response.data);
        setFactorUnitDenominatorListLoading(false);
      },
    });
  }

  /* API to get region */
  function getRegion() {
    setRegionListLoading(true);
    fetchWrapper({
      endpoint_url: "emission_factor_management/getRegionList",
      onSuccess: (response) => {
        setRegionList(response.data);
        setRegionListLoading(false);
      },
    });
  }

  /* API to add emission source */
  function addEmissionFactor(user_inputs) {
    setLoadingSubmitButton(true);
    fetchWrapper({
      endpoint_url: "emission_factor_management/addEmissionFactor",
      body: {
        scope_id: user_inputs.ScopeID,
        scope_type_id: user_inputs.ScopeTypeID,
        category_type_id: user_inputs.CategoryTypeID
          ? user_inputs.CategoryTypeID
          : null,
        emission_source: user_inputs.EmissionSource,
        dimension_id: user_inputs.DimensionID,
        gas_type_id: user_inputs.GasTypeID,
        factor_year: user_inputs.FactorYear.format("YYYY"),
        factor_source: user_inputs.FactorSource,
        factor_type: user_inputs.FactorType,
        factor_unit_denominator_id:
          user_inputs.FactorUnit.FactorUnitDenominatorID,
        factor_value: user_inputs.FactorValue,
        region_id: user_inputs.RegionID,
        is_biogenic: user_inputs.IsBiogenic,
        remark: user_inputs.Remark ? user_inputs.Remark : null,
      },
      onSuccess: (response) => {
        if (response.data.status) {
          messageApi.open({
            type: "success",
            content: response.message,
          });
        } else {
          messageApi.open({
            type: "error",
            content: response.message,
          });
        }

        setIsEmissionFactorFormModalOpen(false);
        emissionFactorForm.resetFields();
        setLoadingSubmitButton(false);
        refreshTable();
      },
    });
  }

  /* API to update edited emission factor */
  function editEmissionFactor(user_inputs) {
    setLoadingSubmitButton(true);
    fetchWrapper({
      endpoint_url: "emission_factor_management/editEmissionFactor",
      body: {
        emission_factor_id: initialValues.ID,
        emission_source_id: initialValues.EmissionSourceID,
        factor_year: user_inputs.FactorYear.format("YYYY"),
        factor_source: user_inputs.FactorSource,
        factor_type: user_inputs.FactorType,
        factor_unit_denominator_id:
          user_inputs.FactorUnit.FactorUnitDenominatorID,
        factor_value: user_inputs.FactorValue,
        region_id: user_inputs.RegionID,
        is_biogenic: user_inputs.IsBiogenic,
        remark: user_inputs.Remark ? user_inputs.Remark : null,
      },
      onSuccess: (response) => {
        if (response.data.status === true) {
          messageApi.open({
            type: "success",
            content: response.message,
          });
          setIsEmissionFactorFormModalOpen(false);
          emissionFactorForm.resetFields();
        } else {
          messageApi.open({
            type: "error",
            content: response.message,
          });
        }
        setLoadingSubmitButton(false);
        refreshTable();
      },
    });
  }

  useEffect(() => {
    getFullEmissionSource();
    getRegion();
    getGasType();
  }, []);

  useEffect(() => {
    const uniqueScopeList = [
      ...new Map(
        fullEmissionSourceList.map((item) => [
          item.ScopeID,
          {
            value: item.ScopeID,
            label: item.ScopeName,
          },
        ])
      ).values(),
    ];

    setScopeList(uniqueScopeList);
  }, [fullEmissionSourceList]);

  useEffect(() => {
    if (mode === "add") {
      setScopeTypeSelected(null);
      emissionFactorForm.setFieldValue("ScopeTypeID", null);
    }

    if (scopeSelected) {
      const uniqueScopeTypeList = [
        ...new Map(
          fullEmissionSourceList
            .filter((item) => item.ScopeID === scopeSelected)
            .map((item) => [
              item.ScopeTypeID,
              {
                value: item.ScopeTypeID,
                label: item.ScopeTypeDisplayName,
              },
            ])
        ).values(),
      ];

      setScopeTypeList(uniqueScopeTypeList);
    }
  }, [scopeSelected, fullEmissionSourceList]);

  useEffect(() => {
    if (mode === "add") {
      setCategoryTypeSelected(null);
      emissionFactorForm.setFieldValue("CategoryTypeID", null);
    }

    if (scopeSelected && scopeTypeSelected) {
      const uniqueCategoryTypeList = [
        ...new Map(
          fullEmissionSourceList
            .filter(
              (item) =>
                item.ScopeID === scopeSelected &&
                item.ScopeTypeID === scopeTypeSelected &&
                item.CategoryType !== null
            )
            .map((item) => [
              item.CategoryTypeID,
              {
                value: item.CategoryTypeID,
                label: item.CategoryTypeDisplayName,
              },
            ])
        ).values(),
      ];

      setCategoryTypeList(uniqueCategoryTypeList);
    }
  }, [scopeSelected, scopeTypeSelected, fullEmissionSourceList]);

  useEffect(() => {
    if (mode === "add") {
      setEmissionSourceSelected(null);
      emissionFactorForm.setFieldValue("EmissionSource", null);
    }
    if (scopeSelected && scopeTypeSelected) {
      setEmissionSourceList(
        Object.values(
          fullEmissionSourceList
            .filter(
              (item) =>
                item.ScopeID === scopeSelected &&
                item.ScopeTypeID === scopeTypeSelected &&
                item.CategoryTypeID === categoryTypeSelected
            )
            // remove duplicate
            .reduce((acc, item) => {
              acc[item.EmissionSource] ??= {
                value: item.EmissionSource,
                label: item.EmissionSource,
              };
              return acc;
            }, {})
        )
      );
    }
  }, [
    scopeSelected,
    scopeTypeSelected,
    categoryTypeSelected,
    fullEmissionSourceList,
  ]);

  useEffect(() => {
    if (mode === "add") {
      setDimensionSelected(null);
      emissionFactorForm.setFieldValue("DimensionID", null);
    }

    if (emissionSourceSelected) {
      setDimensionList(
        fullEmissionSourceList
          .filter((item) => item.EmissionSource === emissionSourceSelected)
          .map((item) => ({
            value: item.DimensionID,
            label: item.Dimension,
          }))
      );
    }
  }, [emissionSourceSelected, fullEmissionSourceList]);

  // useEffect(() => {
  //   if (mode === "add") {
  //     setGasTypeSelected(null);
  //     emissionFactorForm.setFieldValue("GasTypeID", null);
  //   }

  //   if (dimensionSelected && emissionSourceSelected) {
  //     getGasType();
  //   }
  // }, [dimensionSelected, emissionSourceSelected, fullEmissionSourceList]);

  useEffect(() => {
    if (mode === "add") {
      setFactorUnitDenominatorSelected(null);
      emissionFactorForm.setFieldValue(
        ["FactorUnit", "FactorUnitDenominatorID"],
        null
      );
    }

    if (dimensionSelected) {
      getFactorUnitDenominator();
    }
  }, [emissionSourceSelected, dimensionSelected, fullEmissionSourceList]);

  useEffect(() => {
    if (mode === "edit") {
      if (initialValues) {
        initialValues.FactorYear = dayjs(
          String(initialValues.FactorYear),
          "YYYY"
        );
        emissionFactorForm.setFieldsValue(initialValues);
        emissionFactorForm.setFieldValue(
          ["FactorUnit", "FactorUnitDenominatorID"],
          initialValues?.FactorValueUnitDenominatorID ?? null
        );

        setScopeSelected(initialValues?.ScopeID ?? null);
        setScopeTypeSelected(initialValues?.ScopeTypeID ?? null);
        setCategoryTypeSelected(
          initialValues?.CategoryTypeID
            ? initialValues.CategoryTypeID === "-"
              ? null
              : initialValues.CategoryTypeID
            : null
        );
        setEmissionSourceSelected(initialValues?.EmissionSource ?? null);
        setDimensionSelected(initialValues?.DimensionID ?? null);
        setGasTypeSelected(initialValues?.GasTypeID ?? null);
        setFactorUnitDenominatorSelected(
          initialValues?.FactorValueUnitDenominatorID ?? null
        );
      }
    }
  }, [mode, initialValues]);

  return (
    <>
      <Form
        form={emissionFactorForm}
        name="emission-factor-form"
        labelCol={{
          span: 10,
        }}
        wrapperCol={{
          span: 14,
        }}
        onFinish={(values) => {
          if (mode === "add") {
            addEmissionFactor(values);
          } else {
            editEmissionFactor(values);
          }
        }}
        autoComplete="off"
      >
        <Form.Item
          label="Scope"
          name="ScopeID"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select scope.",
            },
          ]}
        >
          <Select
            placeholder="Select scope"
            loading={scopeListLoading}
            disabled={mode === "edit"}
            value={scopeSelected}
            options={scopeList}
            onChange={(value) => setScopeSelected(value)}
          />
        </Form.Item>

        <Form.Item
          label="Scope Type"
          name="ScopeTypeID"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select scope type.",
            },
          ]}
        >
          <Select
            placeholder="Select scope type"
            loading={scopeTypeListLoading}
            disabled={!scopeSelected || mode === "edit"}
            value={scopeTypeSelected}
            options={scopeTypeList}
            onChange={(value) => setScopeTypeSelected(value)}
          />
        </Form.Item>

        {categoryTypeList.length !== 0 && (
          <Form.Item
            label="Category Type"
            name="CategoryTypeID"
            rules={[
              {
                required:
                  requiredCategoryTypeScopeTypeList.includes(scopeTypeSelected),
                message: "You must select category type.",
              },
            ]}
          >
            <Select
              placeholder="Select category type"
              loading={categoryTypeListLoading}
              disabled={
                !scopeTypeSelected ||
                categoryTypeList.length === 0 ||
                mode === "edit"
              }
              value={categoryTypeSelected}
              options={categoryTypeList}
              onChange={(value) => setCategoryTypeSelected(value)}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Emission Source"
          name="EmissionSource"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "Emission source cannot be empty.",
            },
          ]}
        >
          <Select
            placeholder="Select emission source"
            loading={emissionSourceListLoading}
            disabled={
              !scopeTypeSelected ||
              mode === "edit" ||
              (categoryTypeList.length > 0 && !categoryTypeSelected)
            }
            value={emissionSourceSelected}
            options={emissionSourceList}
            onChange={(value) => setEmissionSourceSelected(value)}
            showSearch={{
              optionFilterProp: "label",
              filterSort: (optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase()),
            }}
          />
        </Form.Item>

        <Form.Item
          label="Dimension"
          name="DimensionID"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select dimension.",
            },
          ]}
        >
          <Select
            placeholder="Select dimension"
            disabled={!emissionSourceSelected || mode === "edit"}
            loading={dimensionListLoading}
            value={dimensionSelected}
            options={dimensionList}
            onChange={(value) => setDimensionSelected(value)}
          />
        </Form.Item>

        <Form.Item
          label="Gas Type of Emission Factor"
          name="GasTypeID"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select gas type.",
            },
          ]}
        >
          <Select
            placeholder="Select gas type"
            loading={gasTypeListLoading}
            disabled={mode === "edit" || !dimensionSelected}
            value={gasTypeSelected}
            options={gasTypeList}
            onChange={(value) => setGasTypeSelected(value)}
          />
        </Form.Item>

        <Form.Item
          label="Factor Year"
          name="FactorYear"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select factor year.",
            },
          ]}
        >
          <DatePicker
            picker="year"
            style={{ width: "100%" }}
            disabled={mode === "edit"}
          />
        </Form.Item>

        <Form.Item
          label="Factor Source"
          name="FactorSource"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must input factor source.",
            },
          ]}
        >
          <Input placeholder="Input factor source" />
        </Form.Item>

        <Form.Item
          label="Factor Type"
          name="FactorType"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must input factor type.",
            },
          ]}
        >
          <Input placeholder="Input factor type" />
        </Form.Item>

        <Form.Item
          label={
            <>
              {/* hardcode asterisk as it does not show up */}
              <span style={{ color: "red" }}>*&nbsp;</span>
              <span>Factor Value Unit (Denominator)</span>
            </>
          }
          style={{
            margin: "0px",
          }} /* hardcode margin so that the space does not look weird due to Space.Compact */
        >
          <Space.Compact style={{ width: "100%" }}>
            <Button disabled style={{ width: "30%" }}>
              kg/
            </Button>
            <Form.Item
              name={["FactorUnit", "FactorUnitDenominatorID"]}
              rules={[
                {
                  required: FORM_REQUIRED,
                  message: "You must select denominator for factor unit.",
                },
              ]}
              style={{ width: "70%" }}
            >
              <Select
                placeholder="Select unit"
                loading={factorUnitDenominatorListLoading}
                disabled={!dimensionSelected}
                value={factorUnitDenominatorSelected}
                options={factorUnitDenominatorList}
                onChange={(value) => setFactorUnitDenominatorSelected(value)}
              />
            </Form.Item>
          </Space.Compact>
        </Form.Item>

        <Form.Item
          label="Factor Value"
          name="FactorValue"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must input factor value.",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Input factor value"
          />
        </Form.Item>

        <Form.Item
          label="Region"
          name="RegionID"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select region.",
            },
          ]}
        >
          <Select
            placeholder="Select region"
            loading={regionListLoading}
            value={regionSelected}
            options={regionList}
            onChange={(value) => setRegionSelected(value)}
          />
        </Form.Item>

        <Form.Item
          label="Is The Emission Factor Biogenic?"
          name="IsBiogenic"
          rules={[
            {
              required: FORM_REQUIRED,
              message: "You must select is the emission factor biogenic.",
            },
          ]}
        >
          <Select
            placeholder="Select biogenic status"
            options={[
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ]}
          />
        </Form.Item>

        <Form.Item label="Remark" name="Remark">
          <TextArea
            rows={4}
            placeholder="Input remark (if any)"
            maxLength={100}
            showCount
          />
        </Form.Item>

        <Flex justify="center">
          <Button
            className="mr-2"
            onClick={() => {
              setIsEmissionFactorFormModalOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={loading}
            loading={loadingSubmitButton}
          >
            Submit
          </Button>
        </Flex>
      </Form>
    </>
  );
}

export default EmissionFactorForm;
